import { memo } from 'react';

import { useAnalyticsDebug } from '@air/analytics';
import { usePlayerDebug } from '@air/component-player';
import { Check, MagicWand, Plus, Search, Swap } from '@air/next-icons';
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
} from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';
import { useSysadmin } from '@air/sysadmin';

import { useDevModal } from '~/components/DevModal/hooks/useDevModal';
import { InternalBrowseWorkspaceModal } from '~/components/v2/components/internal/modals/internal-browse-workspace-modal/InternalBrowseWorkspaceModal';
import { useDevToolsStore } from '~/components/v2/store/features/devtools/useDevToolsStore';
import { useCreateWorkspace } from '~/hooks/useCreateWorkspace';

export const WorkspaceHeaderInformationDropdownDevToolsItems = memo(() => {
  const workspaceIdVisible = useDevToolsStore((store) => store.workspaceIdVisible);
  const toggleWorkspaceIdVisible = useDevToolsStore((store) => store.toggleWorkspaceIdVisible);
  const reactQueryDevtoolsVisible = useDevToolsStore((store) => store.reactQueryDevtoolsVisible);
  const toggleReactQueryDevtoolsVisible = useDevToolsStore((store) => store.toggleReactQueryDevtoolsVisible);
  const nodeInspectorVisible = useDevToolsStore((store) => store.nodeInspectorVisible);
  const toggleNodeInspectorVisible = useDevToolsStore((store) => store.toggleNodeInspectorVisible);
  const { handleClearWorkspaceId, isInternal } = useSysadmin();
  const { value: analyticsDebugValue, onToggle: onToggleAnalyticsDebug } = useAnalyticsDebug();
  const { value: playerDebugValue, onToggle: onTogglePlayerDebug } = usePlayerDebug();
  const [showBrowseWorkspaceModal] = useAirModal(InternalBrowseWorkspaceModal);
  const { openDevModal, shouldShowDevButton } = useDevModal();
  const { createWorkspace } = useCreateWorkspace();

  return (
    <>
      <DropdownMenuSeparator />
      <div className="-m-2 flex flex-col gap-2 bg-grey-1 p-2">
        <DropdownMenuLabel>Internal</DropdownMenuLabel>

        {isInternal && (
          <DropdownMenuItem
            label="Switch Workspace"
            prefix={<Swap className="size-4" />}
            onClick={handleClearWorkspaceId}
          />
        )}

        <DropdownMenuItem
          label="Browse workspaces"
          onSelect={showBrowseWorkspaceModal}
          prefix={<Search className="size-4" />}
        />
        {isInternal && (
          <DropdownMenuItem label="Create workspace" onSelect={createWorkspace} prefix={<Plus className="size-4" />} />
        )}

        <DropdownMenuSub className="bg-grey-1" label="DevTools" prefix={<MagicWand className="size-4" />}>
          {shouldShowDevButton && (
            <DropdownMenuItem
              description="Open the developer modal for debugging and testing"
              label="Dev Modal"
              onSelect={openDevModal}
            />
          )}
          <DropdownMenuItem
            description="Toggle Workspace ID visibility in the workspace switcher UI"
            label="Workspace ID"
            onSelect={toggleWorkspaceIdVisible}
            suffix={workspaceIdVisible ? <Check className="size-4" /> : null}
          />
          <DropdownMenuItem
            description="Logs the analytics events to the console for debugging purposes"
            label="Analytics"
            onSelect={onToggleAnalyticsDebug}
            suffix={analyticsDebugValue ? <Check className="size-4" /> : null}
          />
          <DropdownMenuItem
            description="Logs the player events to the console for debugging purposes"
            label="Player"
            onSelect={onTogglePlayerDebug}
            suffix={playerDebugValue ? <Check className="size-4" /> : null}
          />
          <DropdownMenuItem
            description="Toggle canvas node inspector panel visibility"
            label="Canvas Node Inspector"
            onSelect={toggleNodeInspectorVisible}
            suffix={nodeInspectorVisible ? <Check className="size-4" /> : null}
          />
          <DropdownMenuItem
            description="Toggle React Query devtools visibility"
            label="React Query"
            onSelect={toggleReactQueryDevtoolsVisible}
            suffix={reactQueryDevtoolsVisible ? <Check className="size-4" /> : null}
          />
        </DropdownMenuSub>
      </div>
    </>
  );
});

WorkspaceHeaderInformationDropdownDevToolsItems.displayName = 'WorkspaceHeaderInformationDropdownDevToolsItems';
